import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { Proposal, ProposalStage } from "generated/graphql";
import useCustomAddProductsToProposalStage from "hooks/proposals/useCustomAddProductsToProposalStage";
import SelectProductForm, { ProductMeasurement } from "modules/products/forms/SelectProductForm";
import { useCallback } from "react";

type AddProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  stage: ProposalStage;
  onSuccess: (proposal: Proposal) => void;
};

export default function AddProductDrawer({
  open,
  onClose,
  stage,
  onSuccess,
}: AddProductDrawerProps) {
  const [addProductsToProposalStage, { loading, isSuccess, getData }] =
    useCustomAddProductsToProposalStage();

  const handleOnSubmit = useCallback(
    async (measurements: ProductMeasurement[]) => {
      const result = await addProductsToProposalStage({
        variables: {
          input: {
            proposalStageId: stage.id,
            products: measurements.map((measurement) => ({
              id: measurement.id,
              jobSize: parseInt(measurement.jobSize.toString()) || 0,
              dailyUnitsComplete: measurement.dailyUnitsComplete || null,
              jobDuration: parseInt(measurement.jobDuration) || null,
              ...(measurement.mapData && { mapData: measurement.mapData }),
            })),
          },
        },
      });

      const success = isSuccess(result.data);
      const data = getData(result);

      if (success) {
        onSuccess(data as Proposal);
        onClose();
      }

      return { success, data };
    },
    [stage.id, addProductsToProposalStage, isSuccess, getData, onSuccess, onClose]
  );

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Product" width="598px">
      <MDBox p="24px" display="flex" flexDirection="column" gap="16px">
        <UiTypography uiVariant="heading-sm">Product</UiTypography>
        <SelectProductForm
          onSubmit={handleOnSubmit}
          loading={loading}
          mapData={stage.mapData}
          hideSelectionLabel
          useNewDesign
        />
      </MDBox>
    </UiSideModal>
  );
}
